class GalleryMobileControls {
  constructor() {
    // Find all slider components on the page
    this.sliderComponents = document.querySelectorAll('slider-component');
    if (!this.sliderComponents.length) return;
    
    // Check if we're on a mobile device
    this.isMobile = window.innerWidth < 750;
    
    // Initialize controls for each slider component
    this.sliderComponents.forEach(slider => {
      this.initializeControls(slider);
    });
    
    // Listen for resize events to handle orientation changes
    window.addEventListener('resize', this.handleResize.bind(this));
  }
  
  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 750;
    
    // If device switched between mobile and desktop, reinitialize controls
    if (wasMobile !== this.isMobile) {
      this.sliderComponents.forEach(slider => {
        // Remove existing controls
        const existingWrapper = slider.querySelector('.slider-controls-wrapper');
        if (existingWrapper) {
          existingWrapper.remove();
        }
        
        // Reinitialize controls
        this.initializeControls(slider);
      });
    }
  }
  
  initializeControls(sliderComponent) {
    // Find the slider container and media list
    const mediaList = sliderComponent.querySelector('.product__media-list');
    if (!mediaList) return;
    
    // Create a wrapper for the controls that will be positioned relative to the slider
    let controlsWrapper = sliderComponent.querySelector('.slider-controls-wrapper');
    if (!controlsWrapper) {
      controlsWrapper = document.createElement('div');
      controlsWrapper.className = 'slider-controls-wrapper';
      
      // Add mobile-specific class if on mobile
      if (this.isMobile) {
        controlsWrapper.classList.add('mobile-slider-controls');
      }
      
      // Make sure the slider component has proper positioning
      sliderComponent.style.position = 'relative';
      sliderComponent.style.zIndex = '1';
      
      // Create a containing block for absolutely positioned elements
      sliderComponent.style.transform = 'translateZ(0)';
      
      // Append the controls wrapper to the slider component (not the media list)
      sliderComponent.appendChild(controlsWrapper);
      
      // Add a class to the slider component for easier styling
      sliderComponent.classList.add('gallery-slider-initialized');
    }
    
    // Setup the arrow controls for this slider
    this.setupArrowControls(sliderComponent, mediaList, controlsWrapper);
  }

  setupArrowControls(sliderComponent, mediaList, controlsWrapper) {
    // Create and append arrow buttons
    const prevButton = document.createElement('button');
    prevButton.className = 'gallery-mobile-prev';
    prevButton.setAttribute('aria-label', 'Previous image');
    prevButton.setAttribute('type', 'button');
    
    const nextButton = document.createElement('button');
    nextButton.className = 'gallery-mobile-next';
    nextButton.setAttribute('aria-label', 'Next image');
    nextButton.setAttribute('type', 'button');
    
    // Add mobile-specific classes if on mobile
    if (this.isMobile) {
      prevButton.classList.add('mobile-button');
      nextButton.classList.add('mobile-button');
    }
    
    // Add buttons to the controls wrapper
    controlsWrapper.appendChild(prevButton);
    controlsWrapper.appendChild(nextButton);

    // Track scrolling state
    sliderComponent.isScrolling = false;

    // Find all media items in the media list
    const mediaItems = Array.from(mediaList.querySelectorAll('.product__media-item'));
    if (mediaItems.length <= 1) return; // No need for controls if only one item
    
    // Add event handlers with better mobile support
    const addMultipleEventListeners = (element, events, handler) => {
      events.forEach(event => {
        element.addEventListener(event, handler, { passive: false });
      });
    };

    // Prevent default on touch events to avoid scrolling issues
    const preventDefaultForEvent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    
    // Track current slide index for navigation
    sliderComponent.currentSlideIndex = 0;
    
    // Special handling for touch events
    let touchStartX = 0;
    let touchEndX = 0;
    
    // Add touchstart event to track initial touch position
    if (this.isMobile) {
      mediaList.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      
      // Add touchend event to handle swipe navigation
      mediaList.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const touchDiff = touchEndX - touchStartX;
        
        // If significant horizontal swipe detected
        if (Math.abs(touchDiff) > 50) {
          if (touchDiff > 0) {
            // Swipe right - go to previous
            this.navigate(sliderComponent, mediaList, mediaItems, 'prev');
          } else {
            // Swipe left - go to next
            this.navigate(sliderComponent, mediaList, mediaItems, 'next');
          }
        }
      }, { passive: false });
      
      // Listen for scroll events to update button visibility
      mediaList.addEventListener('scroll', () => {
        this.updateButtonsVisibility(sliderComponent, mediaList, mediaItems);
      }, { passive: true });
    }

    // Add click and touch handlers for previous button
    addMultipleEventListeners(prevButton, ['click', 'touchend'], (e) => {
      preventDefaultForEvent(e);
      this.navigate(sliderComponent, mediaList, mediaItems, 'prev');
    });

    // Add click and touch handlers for next button
    addMultipleEventListeners(nextButton, ['click', 'touchend'], (e) => {
      preventDefaultForEvent(e);
      this.navigate(sliderComponent, mediaList, mediaItems, 'next');
    });

    // Initial update to highlight the active slide
    this.updateActiveSlide(sliderComponent, mediaList, mediaItems);
    
    // Initial update of button visibility
    this.updateButtonsVisibility(sliderComponent, mediaList, mediaItems);
  }

  updateActiveSlide(sliderComponent, mediaList, mediaItems) {
    if (!mediaItems || mediaItems.length === 0) return;
    
    // Find the currently active slide
    const activeSlide = mediaItems.find(item => item.classList.contains('is-active'));
    
    // If no active slide is found, set the first one as active
    if (!activeSlide && mediaItems.length > 0) {
      mediaItems[0].classList.add('is-active');
      sliderComponent.currentSlideIndex = 0;
    } else if (activeSlide) {
      sliderComponent.currentSlideIndex = mediaItems.indexOf(activeSlide);
    }
  }
  
  updateButtonsVisibility(sliderComponent, mediaList, mediaItems) {
    // Get the controls wrapper
    const controlsWrapper = sliderComponent.querySelector('.slider-controls-wrapper');
    if (!controlsWrapper) return;
    
    // Get the previous and next buttons
    const prevButton = controlsWrapper.querySelector('.gallery-mobile-prev');
    const nextButton = controlsWrapper.querySelector('.gallery-mobile-next');
    
    // Calculate which slide is most visible in the viewport
    const mediaListRect = mediaList.getBoundingClientRect();
    const mediaListCenter = mediaListRect.left + mediaListRect.width / 2;
    
    let closestSlideIndex = 0;
    let closestDistance = Infinity;
    
    mediaItems.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(mediaListCenter - itemCenter);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlideIndex = index;
      }
    });
    
    // Update the current slide index
    sliderComponent.currentSlideIndex = closestSlideIndex;
    
    // Update active state
    mediaItems.forEach((item, index) => {
      if (index === closestSlideIndex) {
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
      }
    });
  }

  navigate(sliderComponent, mediaList, mediaItems, direction) {
    // Don't navigate if already scrolling
    if (sliderComponent.isScrolling) return;
    
    // Get current index
    const currentIndex = sliderComponent.currentSlideIndex || 0;
    let targetIndex;

    // Determine the target slide index based on direction
    if (direction === 'prev') {
      targetIndex = currentIndex > 0 ? currentIndex - 1 : mediaItems.length - 1;
    } else {
      targetIndex = currentIndex < mediaItems.length - 1 ? currentIndex + 1 : 0;
    }

    const targetSlide = mediaItems[targetIndex];
    if (targetSlide) {
      // Mark as scrolling
      sliderComponent.isScrolling = true;
      
      // Update the current slide index
      sliderComponent.currentSlideIndex = targetIndex;
      
      // Remove active class from all slides
      mediaItems.forEach(item => item.classList.remove('is-active'));
      
      // Add active class to target slide
      targetSlide.classList.add('is-active');
      
      // Scroll to the target slide
      targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      
      // Update any media gallery component if present
      const mediaId = targetSlide.dataset.mediaId;
      if (mediaId) {
        const mediaGallery = sliderComponent.querySelector('media-gallery');
        if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
          mediaGallery.setActiveMedia(mediaId, false);
        }
      }
      
      // Reset scrolling state after animation completes
      setTimeout(() => {
        sliderComponent.isScrolling = false;
        // Update button visibility after scrolling completes
        this.updateButtonsVisibility(sliderComponent, mediaList, mediaItems);
      }, 500);
    }
  }
}

// Initialize the controls when the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new GalleryMobileControls());
} else {
  new GalleryMobileControls();
}
